import _ from 'underscore';

import {
  ACCESS_LEVEL_MULTIPLE_VALUES,
  ACCESS_LEVELS,
  AccessLevel,
  AccessLevelToDisplay,
  Capability,
  EntityCapability,
} from 'auth/capabilities/CapabilityConstants';
import {
  Capability as CapabilityEnum,
  CAPABILITY_ONBOARDING_ORDER,
  UNIMPLEMENTED_CAPABILITIES,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {getInDevelopmentCapabilities} from 'auth/capabilities/InDevelopmentCapabilities';
import BenchlingConfig from 'BenchlingConfig';
import {UserAccessLevel} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CapabilityManagementViewCapability} from 'tenants/admin/__generated/CapabilityManagementViewCapability';
import sortedBy from 'util/sortedBy';

function getAccessLevelForDropdown(
  capabilityKey: string,
  capabilityKeyToDistinctAccessLevels: Map<string, Set<UserAccessLevel>>
): AccessLevelToDisplay {
  const accessLevelsSet = capabilityKeyToDistinctAccessLevels.get(capabilityKey);
  if (!accessLevelsSet || accessLevelsSet.size === 0) {
    return AccessLevel.NO_ACCESS;
  }
  return accessLevelsSet.size > 1
    ? ACCESS_LEVEL_MULTIPLE_VALUES
    : (accessLevelsSet.values().next().value as unknown as AccessLevelToDisplay);
}

export function makeEditableCapabilitiesForDisplay(
  capabilityKeyToDistinctAccessLevels: Map<string, Set<UserAccessLevel>>,
  tenantCapabilities: Array<CapabilityManagementViewCapability>,
  includeDisabledCapabilities: boolean
): Array<Required<Capability>> {
  return tenantCapabilities
    .filter((capability) => includeDisabledCapabilities || capability.isEnabled)
    .map((capability) => ({
      ...capability,
      accessLevel: getAccessLevelForDropdown(capability.key, capabilityKeyToDistinctAccessLevels),
      edited: false,
    }));
}

export function makeCapabilityKeyToDistinctAccessLevels(
  tenantCapabilities: Array<CapabilityManagementViewCapability>,
  entityCapabilities: Array<EntityCapability>,
  numEntities: number
): Map<string, Set<UserAccessLevel>> {
  const capabilityKeyToAllAccessLevels = makeCapabilityKeyToAllAccessLevels(
    tenantCapabilities,
    entityCapabilities
  );
  const capabilityKeyToDistinctAccessLevels = new Map<string, Set<UserAccessLevel>>();
  capabilityKeyToAllAccessLevels.forEach((accessLevels: Array<UserAccessLevel>, key: string) => {
    if (accessLevels.length < numEntities) {
      accessLevels.push(UserAccessLevel.NO_ACCESS);
    }
    capabilityKeyToDistinctAccessLevels.set(key, new Set(accessLevels));
  });
  return capabilityKeyToDistinctAccessLevels;
}

function makeCapabilityKeyToAllAccessLevels(
  tenantCapabilities: Array<CapabilityManagementViewCapability>,
  entityCapabilities: Array<EntityCapability>
): Map<string, Array<UserAccessLevel>> {
  return tenantCapabilities.reduce((map, tenantCapability: CapabilityManagementViewCapability) => {
    const accessLevels = entityCapabilities
      .filter((entityCapability) => entityCapability.capability.key === tenantCapability.key)
      .map((capability) => capability.accessLevel);
    map.set(tenantCapability.key, accessLevels);
    return map;
  }, new Map<string, Array<UserAccessLevel>>());
}

export function isAccessLevelHigher(first: AccessLevel, second: AccessLevel): boolean {
  return ACCESS_LEVELS.indexOf(first) > ACCESS_LEVELS.indexOf(second);
}

export function filterCapabilitiesFromBeingProvisioned(
  capabilities: Array<Required<Capability>>
): Array<Required<Capability>> {
  return capabilities.filter(
    (capability) =>
      capability.accessLevel !== ACCESS_LEVEL_MULTIPLE_VALUES &&
      !UNIMPLEMENTED_CAPABILITIES.includes(capability.key as CapabilityEnum.NOTEBOOK)
  );
}

function filterToReleasedCapabilites(capabilities: Array<Capability>): Array<Capability> {
  return capabilities.filter(
    (capability) =>
      BenchlingConfig.IDENTITY_ENABLE_GATING_AND_PROVISIONING_IN_DEVELOPMENT_CAPABILITIES ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !getInDevelopmentCapabilities().includes(capability.key as any)
  );
}

export function sortReleasedCapabilitiesForDisplay(capabilities: Array<Capability>): Array<Capability> {
  return sortedBy(sortReleasedCapabilitiesByOnboardingOrder(capabilities), [
    {accessor: 'isEnabled', reversed: true},
  ]);
}

export function sortReleasedCapabilitiesByOnboardingOrder(
  capabilities: Array<Capability>
): Array<Capability> {
  const releasedCapabilities = filterToReleasedCapabilites(capabilities);
  const sortedReleasedCapabilities = sortCapabilitiesByOnboardingOrder(releasedCapabilities);
  return sortedReleasedCapabilities;
}

export function makeCapabilityFromEntityCapability(entityCapability: EntityCapability): Required<Capability> {
  return {
    ...entityCapability.capability,
    accessLevel: entityCapability.accessLevel as unknown as AccessLevel,
    edited: entityCapability.capability.edited ?? false,
  };
}

function sortCapabilitiesByOnboardingOrder(capabilities: Array<Capability>): Array<Capability> {
  return capabilities
    .slice()
    .sort(
      (a: Capability, b: Capability): number =>
        CAPABILITY_ONBOARDING_ORDER.indexOf(a.key as CapabilityEnum) -
        CAPABILITY_ONBOARDING_ORDER.indexOf(b.key as CapabilityEnum)
    );
}

export function haveCapabilitiesChanged(
  adjustedCapabilities: Array<Capability>,
  originalCapabilities: Array<Capability>
): boolean {
  return _.any(adjustedCapabilities, (adjustedCapability) => {
    const originalCapability = originalCapabilities.find((c) => c.key === adjustedCapability.key)!;
    return adjustedCapability.accessLevel !== originalCapability.accessLevel;
  });
}
